import {
  IMediaCheckKindResult,
  IMediaCheckResult,
  IMediaStream,
  IMediaStreamDefaults,
} from 'components/UserMedia/IUserMedia';
import { EUserMediaEvent, TUserMediaEvents } from 'components/UserMedia/UserMedia.events';
import { EventBus } from 'components/eventBus';
import { CustomEventTarget } from 'components/eventTarget/EventTarget';
import { Logger, TLogger } from 'components/logger/Logger';
import { TLoggerEvents } from 'components/logger/Logger.events';
import { LoggerConsoleTransport } from 'components/logger/LoggerConsoleTransport';
import { resolveGetUserMedia } from 'utils/WebRTCCommon';
import { bind } from 'utils/bind';

import { getAudioTrackConstraints } from './helpers/constraints/getAudioTrackConstraints';
import { getVideoTrackConstraints } from './helpers/constraints/getVideoTrackConstraints';
import { IDevices } from './helpers/enumerateDevices/IEnumerateDevices';
import { enumerateDevices } from './helpers/enumerateDevices/enumerateDevices';
import { IRejectReason, requestMediaPermissions } from './helpers/meidaCheck/mediaCheck';
import { isStreamInactive } from './helpers/track/isStreamInactive';
import { stopStream } from './helpers/track/stopStream';

export class UserMedia extends CustomEventTarget<TUserMediaEvents> {
  private _currentMediaStream: IMediaStream | null = null;
  private _mediaCheckCache: IMediaCheckResult | null = null;

  private _mediaDevices: IDevices | null = null;
  private readonly _eventBus = new EventBus<TLoggerEvents>();
  private readonly _logger: TLogger = Logger(this._eventBus, 'UserMedia');
  private readonly _loggerTransport: LoggerConsoleTransport = new LoggerConsoleTransport(
    this._eventBus,
  );

  constructor() {
    super();

    this._logger.info('Created');

    navigator.mediaDevices.addEventListener('devicechange', this._onDevicesChanged);
  }

  public destroy(): void {
    navigator.mediaDevices.removeEventListener('devicechange', this._onDevicesChanged);
    this._logger.info('Destroyed');

    this._stopCurrentStream();
  }

  public async requestPermissions(): Promise<IMediaCheckResult> {
    this._logger.info('Requesting permissions');
    if (this._mediaCheckCache) {
      this._logger.debug('Has previous check results cached. Returning it');
      this._logger.info('Check results', { ...this._mediaCheckCache });
      return this._mediaCheckCache;
    }

    try {
      this._logger.verbose('Request both video and audio permissions');
      await requestMediaPermissions({ video: true, audio: true });

      this._logger.debug(
        'No exceptions thrown while checking permissions. Saving both as available in cache',
      );

      this._mediaCheckCache = {
        video: {
          isAvailable: true,
          error: null,
        },
        audio: {
          isAvailable: true,
          error: null,
        },
      };

      this._logger.info('Check results', { ...this._mediaCheckCache });

      return this._mediaCheckCache;
    } catch (e) {
      this._logger.debug(
        'Got an exception while checking both audio and video permissions. Checking them individually',
        { exception: (e as Error).toString() },
      );
      const checkResults: IMediaCheckResult = {
        video: {
          isAvailable: false,
          error: null,
        },
        audio: {
          isAvailable: false,
          error: null,
        },
      };

      checkResults.video = await this._checkKindPermissions({ video: true, audio: false });
      this._logger.verbose(`Video permissions result: ${checkResults.video.isAvailable}`);
      checkResults.audio = await this._checkKindPermissions({ video: false, audio: true });
      this._logger.verbose(`Audio permissions result: ${checkResults.audio.isAvailable}`);

      this._mediaCheckCache = checkResults;

      this._logger.debug(`Got individual check results`, { ...checkResults });
      this._logger.info('Check results', { ...this._mediaCheckCache });

      return this._mediaCheckCache;
    }
  }

  public async getVideoDevices(): Promise<MediaDeviceInfo[]> {
    this._logger.info('Getting video devices list');
    const devices: IDevices = await this._getMediaDevices();
    this._logger.info(`Got video devices`, devices);

    return devices.videoInputs;
  }

  public async getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    this._logger.info('Getting audio input devices list');
    const devices: IDevices = await this._getMediaDevices();
    this._logger.info('Got audio input devices', devices);

    return devices.audioInputs;
  }

  public async getAudioOutputDevices(): Promise<MediaDeviceInfo[]> {
    this._logger.info('Getting audio output devices list');
    const devices: IDevices = await this._getMediaDevices();
    this._logger.info('Got audio output devices', devices);
    return devices.audioOutputs;
  }

  public async getMediaStream(
    constraints: MediaStreamConstraints,
    defaults: IMediaStreamDefaults,
  ): Promise<MediaStream> {
    this._logger.info('Getting media stream', { constraints, defaults });
    const mappedConstraints: MediaStreamConstraints = {};

    if (constraints.audio) {
      this._logger.debug('Audio constraints are specified in requested constraints. Mapping it');
      mappedConstraints.audio = getAudioTrackConstraints(constraints.audio);
      this._logger.debug('Audio constraints mapped', { constraints: mappedConstraints.audio });
    }

    if (constraints.video) {
      this._logger.debug('Video constraints are specified in requested constraints. Mapping it');
      mappedConstraints.video = getVideoTrackConstraints(constraints.video, defaults);
      this._logger.debug('Video constraints mapped', { constraints: mappedConstraints.video });
    }

    this._logger.info('Final constraints for media stream', { mappedConstraints });

    if (!this._currentMediaStream) {
      this._logger.debug('No current media stream known. Creating new stream');
      const mediaStream: MediaStream = await this._createNewStream(mappedConstraints);
      this._logger.info('Media stream is ready', { mediaStream });
      return mediaStream;
    }

    const isStreamActive: boolean = !isStreamInactive(this._currentMediaStream.stream);
    if (isStreamActive && (await this._isCurrentStreamSatisfiesConstraints(mappedConstraints))) {
      this._logger.debug(
        'Current stream is active and satisfies constraints. Returning cached one',
      );
      this._logger.info('Media stream is ready', { mediaStream: this._currentMediaStream.stream });
      return this._currentMediaStream.stream;
    }

    this._logger.debug(
      `Current stream ${
        isStreamActive ? 'does not satisfies constraints' : 'is inactive'
      }. Creating new stream`,
    );

    await this._stopCurrentStream();

    const mediaStream: MediaStream = await this._createNewStream(mappedConstraints);
    this._logger.info('Media stream is ready', { mediaStream });
    return mediaStream;
  }

  private async _stopCurrentStream(): Promise<void> {
    this._logger.debug('Stopping current media stream');
    if (!this._currentMediaStream) {
      this._logger.verbose('No current stream known. Skipping');
      return;
    }

    await stopStream(this._currentMediaStream.stream);
    this._currentMediaStream = null;
  }

  private async _checkKindPermissions(
    constraints: MediaStreamConstraints,
  ): Promise<IMediaCheckKindResult> {
    this._logger.debug('Checking kind permissions', { constraints });
    const checkResults: IMediaCheckKindResult = {
      isAvailable: false,
      error: null,
    };

    try {
      this._logger.verbose('Requesting media permissions for constraints', { constraints });
      checkResults.isAvailable = await requestMediaPermissions(constraints);
      this._logger.verbose('Successfully got permissions for constraints', { constraints });
    } catch (e) {
      this._logger.verbose('Could not get permissions for constraints', { constraints, error: e });
      checkResults.isAvailable = false;
      checkResults.error = e as IRejectReason;
    }

    this._logger.debug('Final result of permissions check', { constraints, checkResults });
    return checkResults;
  }

  private async _getMediaStream(constraints: MediaStreamConstraints): Promise<MediaStream> {
    this._logger.debug('Getting media stream with constraints', { constraints });
    const getUserMedia: typeof navigator.mediaDevices.getUserMedia = resolveGetUserMedia();

    const stream: MediaStream = await getUserMedia(constraints);

    this._currentMediaStream = {
      constraints,
      stream,
    };

    this._logger.debug('Resulting media stream', { mediaStream: this._currentMediaStream.stream });

    return this._currentMediaStream.stream;
  }

  private async _createNewStream(constraints: MediaStreamConstraints): Promise<MediaStream> {
    this._logger.debug('Creating new media stream with constraints', { constraints });
    try {
      return await this._getMediaStream(constraints);
    } catch (error: any) {
      this._logger.warn(`Got an error while creating new media stream`, { error, constraints });
      if (error.name === 'OverconstrainedError' && error.constraint === 'deviceId') {
        this._logger.verbose('Trying to get stream with no audio deviceId specified');
        const newConstraints = {
          ...constraints,
          audio: true,
        };

        return await this._getMediaStream(newConstraints);
      }

      throw error;
    }
  }

  private async _getMediaDevices(): Promise<IDevices> {
    this._logger.verbose('Getting media devices');
    if (this._mediaDevices) {
      this._logger.verbose('Has media devices in cache, returning them', {
        devices: this._mediaDevices,
      });
      return this._mediaDevices;
    }

    this._mediaDevices = await enumerateDevices();
    return this._mediaDevices;
  }

  private async _isCurrentStreamSatisfiesConstraints(
    constraints: MediaStreamConstraints,
  ): Promise<boolean> {
    this._logger.debug('Checking if current media stream satisfies constraints', {
      constraints,
      mediaStream: this._currentMediaStream,
    });
    if (!this._currentMediaStream) {
      this._logger.verbose('No current media stream available');
      return false;
    }

    try {
      if (constraints.video) {
        this._logger.verbose('Checking video track constraints');
        const videoTrack: MediaStreamTrack = this._currentMediaStream.stream.getVideoTracks()[0];

        if (!videoTrack && constraints.video) {
          this._logger.verbose('No video track in media stream and constraints has video');
          return false;
        }

        if (videoTrack && !constraints.video) {
          this._logger.verbose('There is video track in media stream and constraints has no video');
          return false;
        }

        if (
          typeof constraints.video === 'object' &&
          constraints.video.deviceId &&
          videoTrack.getCapabilities().deviceId !== constraints.video.deviceId
        ) {
          this._logger.verbose(
            'Constraints has video device ID and it not matches current device id',
          );
          return false;
        }

        if (videoTrack && typeof constraints.video !== 'boolean') {
          this._logger.verbose('Trying to apply new constraints for video');
          await videoTrack.applyConstraints(constraints.video);
        }
      }
    } catch (e) {
      this._logger.debug('Could not apply constraints or get capabilities of video track', {
        error: e,
      });
      return false;
    }

    try {
      if (constraints.audio) {
        this._logger.verbose('Checking audio track constraints');
        const audioTrack: MediaStreamTrack = this._currentMediaStream.stream.getAudioTracks()[0];

        if (!audioTrack && constraints.audio) {
          this._logger.verbose('No audio track in media stream and constraints has audio');
          return false;
        }

        if (audioTrack && !constraints.audio) {
          this._logger.verbose('There is audio track in media stream and constraints has no audio');
          return false;
        }

        if (
          typeof constraints.audio === 'object' &&
          constraints.audio.deviceId &&
          audioTrack.getCapabilities().deviceId !== constraints.audio.deviceId
        ) {
          this._logger.verbose(
            'Constraints has audio device ID and it not matches current device id',
          );
          return false;
        }

        if (audioTrack && typeof constraints.audio !== 'boolean') {
          this._logger.verbose('Trying to apply new constraints for audio');
          await audioTrack.applyConstraints(constraints.audio);
        }
      }
    } catch (e) {
      this._logger.debug('Could not apply constraints or get capabilities of audio track', {
        error: e,
      });
      return false;
    }

    return true;
  }

  @bind
  private async _onDevicesChanged(): Promise<void> {
    this._logger.debug('Media devices has changed');
    this._mediaDevices = null;
    const devices: IDevices = await this._getMediaDevices();
    this._mediaDevices = devices;

    this.emitEvent(EUserMediaEvent.MEDIA_DEVICES_UPDATED, devices);
  }
}
